import { authAPI } from './auth';

const API_BASE = '/api/v1/user';

export type TierId = 'free' | 'pro' | 'max';
export type ModelId = 'gpt-5-mini' | 'claude-haiku' | 'gpt-5' | 'gpt-5.2' | 'claude-sonnet' | 'claude-opus';

export interface UserUsage {
  userId: string;
  tier: TierId;
  periodYear: number;
  periodMonth: number;
  analysesCount: number;
  analysesLimit: number;
  tokensUsed: number;
  creditsBalance: number;
  canAnalyze: boolean;
  tierLimits: {
    monthlyAnalyses: number;
    maxDocumentSize: number;
    maxJudgments: number;
    pdfExport: boolean;
    docxExport: boolean;
    priorityProcessing: boolean;
    includedModels: readonly ModelId[];
    extraModels: readonly ModelId[];
  };
}

export interface UsageHistory {
  year: number;
  month: number;
  analysesCount: number;
  tokensUsed: number;
}

export interface Subscription {
  tier: TierId;
  tierName: string;
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  limits: {
    monthlyAnalyses: number;
    maxDocumentSize: number;
    maxJudgments: number;
    pdfExport: boolean;
    docxExport: boolean;
    priorityProcessing: boolean;
    includedModels: readonly ModelId[];
    extraModels: readonly ModelId[];
  };
}

export interface Credits {
  balance: number;
  lifetimePurchased: number;
  pricePerTen: number;
}

export interface TierInfo {
  id: TierId;
  name: string;
  price: number;
  features: {
    monthlyAnalyses: number;
    maxDocumentSize: number;
    maxJudgments: number;
    pdfExport: boolean;
    docxExport: boolean;
    priorityProcessing: boolean;
    includedModels: readonly ModelId[];
    extraModels: readonly ModelId[];
  };
}

export interface TiersResponse {
  tiers: TierInfo[];
  credits: {
    pricePerTen: number;
  };
  modelCredits: Record<ModelId, number>;
  judgmentBoostCredits: Record<number, number>;
}

class UserAPI {
  private async request<T>(endpoint: string): Promise<T> {
    const accessToken = authAPI.getAccessToken();

    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Wystąpił błąd');
    }

    return data;
  }

  async getUsage(): Promise<UserUsage> {
    return this.request<UserUsage>('/usage');
  }

  async getUsageHistory(months: number = 6): Promise<{ history: UsageHistory[] }> {
    return this.request<{ history: UsageHistory[] }>(`/usage/history?months=${months}`);
  }

  async getSubscription(): Promise<Subscription> {
    return this.request<Subscription>('/subscription');
  }

  async getCredits(): Promise<Credits> {
    return this.request<Credits>('/credits');
  }

  async getTiers(): Promise<TiersResponse> {
    const response = await fetch(`${API_BASE}/tiers`);
    return response.json();
  }
}

export const userAPI = new UserAPI();
